import { ref, computed, toValue, type MaybeRefOrGetter } from 'vue'
import axios, { AxiosError } from 'axios'
import { z } from 'zod'

const API_BASE_URL =
  (import.meta.env.VITE_API_URL || '/api').replace(/\/+$/, '')

/* ──────────────────────────────
 * Zod schemas
 * ────────────────────────────── */

const MatchingPremiseSchema = z.object({
  highlight: z.string(),
})

const KeypointArgumentSchema = z.object({
  id: z.string(),
  stance: z.enum(['pro', 'con']).optional(),
  claim: z.string().optional(),
  key_point: z.string().nullable().optional(),
  score: z.number().nullable().optional(),
  quality_score: z.number().nullable().optional(),
  url: z.string().url().optional(),
  matching_premises: z.array(MatchingPremiseSchema).optional(),
}).transform((data) => ({
  ...data,
  score: data.score ?? -1,
  quality_score: data.quality_score ?? 0,
  key_point: data.key_point ?? undefined,
  matching_premises: data.matching_premises ?? [],
}))

const KeypointGroupSchema = z.object({
  key_point: z.string(),
  stance: z.enum(['pro', 'con']).optional(),
  argument_count: z.number(),
  arguments: z.array(KeypointArgumentSchema),
})

const KeypointResponseSchema = z.object({
  total_keypoints: z.number().optional(),
  total_arguments: z.number().optional(),
  took_ms: z.number().optional(),
  keypoints: z.array(KeypointGroupSchema),
})

/* ──────────────────────────────
 * Types
 * ────────────────────────────── */

export type KeypointArgument = z.infer<typeof KeypointArgumentSchema>
export type KeypointGroup = z.infer<typeof KeypointGroupSchema> & { greyed?: boolean; noKeyPoint?: boolean }
export type KeypointResponse = z.infer<typeof KeypointResponseSchema>

/* ──────────────────────────────
 * Error type
 * ────────────────────────────── */

type NormalizedError = {
  message: string
  code: number | string
  reason?: string | null
}

type UseKeypointSearchOptions = {
  claimId?: MaybeRefOrGetter<string | null | undefined>
  keypointLimit?: number
  argsPerKeypoint?: number
}

/* ──────────────────────────────
 * Composable
 * ────────────────────────────── */

export function useKeypointSearch(options: UseKeypointSearchOptions = {}) {
  const q = ref('')
  const stance = ref<'all' | 'pro' | 'con'>('all')
  const sortField = ref<'relevance' | 'quality'>('relevance')

  const resp = ref<KeypointResponse | null>(null)
  const baselineResp = ref<KeypointResponse | null>(null)
  const err = ref<NormalizedError | null>(null)
  const loading = ref(false)
  const loadingKeypoint = ref<string | null>(null)
  const loadingKeypointStance = ref<'pro' | 'con' | null>(null)
  const pageSize = options.argsPerKeypoint ?? 5

  // Track which claim+stance combo the baseline was fetched for
  let baselineCacheKey = ''

  let currentAbort: AbortController | null = null
  let seq = 0

  const doSearch = async () => {
    err.value = null
    resp.value = null
    loading.value = true

    if (currentAbort) currentAbort.abort()
    currentAbort = new AbortController()
    const mySeq = ++seq

    try {
      const params: Record<string, unknown> = {
        q: q.value.trim(),
      }

      if (options.keypointLimit) params.keypoint_limit = options.keypointLimit
      if (pageSize) params.args_per_keypoint = pageSize

      const claimId = toValue(options.claimId)
      if (claimId) params.claim_id = String(claimId)

      if (stance.value !== 'all') params.stance = stance.value
      params.sort = sortField.value === 'quality' ? 'quality_score' : 'relevance_score'

      const { data } = await axios.get(
        `${API_BASE_URL}/keypoint-search`,
        { params, signal: currentAbort.signal }
      )

      const parsed = KeypointResponseSchema.safeParse(data)

      if (!parsed.success) {
        console.error('Invalid keypoint response', parsed.error)
        throw {
          message: 'Invalid server response',
          code: 'INVALID_RESPONSE',
          reason: parsed.error.message,
        }
      }

      if (mySeq === seq) {
        resp.value = {
          ...parsed.data,
          keypoints: parsed.data.keypoints.map(kp =>
            kp.key_point === '' ? { ...kp, noKeyPoint: true } : kp
          ),
        }

        // Cache as baseline when searching without query terms
        if (!q.value.trim()) {
          const claimId = toValue(options.claimId)
          baselineCacheKey = `${claimId ?? ''}|${stance.value}`
          baselineResp.value = parsed.data
        }
      }
    } catch (e: any) {
      if (axios.isCancel(e)) return

      if (e?.code === 'INVALID_RESPONSE') {
        if (mySeq === seq) {
          err.value = { message: e.message, code: e.code, reason: e.reason }
        }
        return
      }

      const axErr = e as AxiosError<any>
      if (mySeq === seq) {
        err.value = {
          message: axErr.response?.data?.message ?? axErr.message ?? 'Request failed',
          code: axErr.response?.status ?? axErr.response?.data?.code ?? 'UNKNOWN',
          reason: axErr.response?.data?.reason ?? null,
        }
      }
    } finally {
      if (mySeq === seq) loading.value = false
    }
  }

  /**
   * Fetch baseline keypoints (no query terms) so we can show
   * greyed-out keypoints that have 0 matches for the current query.
   * Results are cached per claim+stance combo.
   */
  const fetchBaseline = async () => {
    const claimId = toValue(options.claimId)
    const cacheKey = `${claimId ?? ''}|${stance.value}`
    if (baselineCacheKey === cacheKey && baselineResp.value) return

    const params: Record<string, unknown> = { q: '' }
    if (options.keypointLimit) params.keypoint_limit = options.keypointLimit
    params.args_per_keypoint = 1
    if (claimId) params.claim_id = String(claimId)
    if (stance.value !== 'all') params.stance = stance.value
    params.sort = 'quality_score'

    try {
      const { data } = await axios.get(`${API_BASE_URL}/keypoint-search`, { params })
      const parsed = KeypointResponseSchema.safeParse(data)
      if (parsed.success) {
        baselineResp.value = parsed.data
        baselineCacheKey = cacheKey
      }
    } catch {
      // Baseline fetch is best-effort; don't block the main search
    }
  }

  const loadMoreForKeypoint = async (keyPoint: string, groupStance?: 'pro' | 'con') => {
    if (!resp.value) return

    const group = resp.value.keypoints.find(kp =>
      kp.key_point === keyPoint && (groupStance === undefined || kp.stance === groupStance)
    )
    if (!group || group.arguments.length >= group.argument_count) return

    loadingKeypoint.value = keyPoint
    loadingKeypointStance.value = groupStance ?? null

    try {
      const params: Record<string, unknown> = {
        q: q.value.trim(),
        key_point: keyPoint,   // always included, even when '' (no-keypoint group)
        limit: pageSize,
        offset: group.arguments.length,
      }

      const claimId = toValue(options.claimId)
      if (claimId) params.claim_id = String(claimId)

      // For no-keypoint groups, pass the group's stance so the server scopes the fetch
      // correctly (e.g. ?key_point=&stance=con). Fall back to the global filter otherwise.
      if (keyPoint === '' && groupStance) {
        params.stance = groupStance
      } else if (stance.value !== 'all') {
        params.stance = stance.value
      }

      params.sort = sortField.value === 'quality' ? 'quality_score' : 'relevance_score'

      const { data } = await axios.get(`${API_BASE_URL}/argument-search`, { params })

      const parsed = z.object({
        hits: z.array(KeypointArgumentSchema),
      }).safeParse(data)

      if (!parsed.success) {
        console.error('Invalid load-more response', parsed.error)
        return
      }

      // Append new arguments into the matching keypoint group
      group.arguments.push(...parsed.data.hits)
    } catch (e: any) {
      if (!axios.isCancel(e)) {
        console.error('Failed to load more arguments for keypoint', e)
      }
    } finally {
      loadingKeypoint.value = null
      loadingKeypointStance.value = null
    }
  }

  const isGroupLoading = (kp: KeypointGroup): boolean => {
    if (loadingKeypoint.value === null) return false
    if (loadingKeypoint.value !== kp.key_point) return false
    if (kp.key_point !== '') return true  // non-empty key points are unique
    const effectiveStance = kp.stance ?? kp.arguments[0]?.stance
    return loadingKeypointStance.value === effectiveStance
  }

  /* ──────────────────────────────
   * Computed helpers
   * ────────────────────────────── */

  const keypoints = computed<KeypointGroup[]>(() =>
    resp.value?.keypoints ?? []
  )

  /** Merge filtered keypoints with baseline to include greyed-out ones with 0 matches */
  const mergedKeypoints = computed<KeypointGroup[]>(() => {
    if (!baselineResp.value || !resp.value) return keypoints.value

    const filteredSet = new Set(keypoints.value.map(kp => kp.key_point))
    const greyed: KeypointGroup[] = baselineResp.value.keypoints
      .filter(kp => !filteredSet.has(kp.key_point))
      .map(kp => ({
        ...kp,
        // Preserve stance from the baseline before clearing arguments
        stance: kp.stance ?? kp.arguments[0]?.stance,
        argument_count: 0,
        arguments: [],
        greyed: true,
      }))

    const all = [...keypoints.value, ...greyed]
    return [
      ...all.filter(kp => kp.key_point !== ''),
      ...all.filter(kp => kp.key_point === ''),
    ]
  })

  const getStance = (kp: KeypointGroup): 'pro' | 'con' | undefined =>
    kp.stance ?? kp.arguments[0]?.stance

  const proKeypoints = computed(() =>
    keypoints.value.filter(kp => getStance(kp) === 'pro')
  )

  const conKeypoints = computed(() =>
    keypoints.value.filter(kp => getStance(kp) === 'con')
  )

  const mergedProKeypoints = computed(() =>
    mergedKeypoints.value.filter(kp => getStance(kp) === 'pro')
  )

  const mergedConKeypoints = computed(() =>
    mergedKeypoints.value.filter(kp => getStance(kp) === 'con')
  )

  return {
    q,
    stance,
    sortField,
    resp,
    err,
    loading,
    isGroupLoading,
    keypoints,
    proKeypoints,
    conKeypoints,
    mergedKeypoints,
    mergedProKeypoints,
    mergedConKeypoints,
    doSearch,
    fetchBaseline,
    loadMoreForKeypoint,
  }
}
